import React from 'react';
import { K, HOSE as DEFAULT_HOSE } from '../constants';
import { calcFL } from '../utils/formulas';
import { NI, Lbl, SecHd, Metric, MathBox } from '../components/ui';
import { usePersist } from '../hooks/usePersist';

const SUPPLY_DEFAULTS = { mode: 'tank', tankGal: 750, tankGPM: 150, tCap: 3000, fill: 1000, dump: 1000, dist: 5, spd: 35, floors: 5, spGPM: 150, spNP: 100 };

export function WaterSupply({ showMath, calcDefaults }) {
  const HOSE = calcDefaults?.hose || DEFAULT_HOSE;
  const floorLossPer = calcDefaults?.ops?.standpipeFloor ?? 5;
  const spAppLoss = calcDefaults?.ops?.standpipeAppliance ?? 25;
  const [s, setS] = usePersist('hydro_supply_inputs', SUPPLY_DEFAULTS);
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const { mode, tankGal, tankGPM, tCap, fill, dump, dist, spd, floors, spGPM, spNP } = { ...SUPPLY_DEFAULTS, ...s };
  const setMode = (v) => set('mode', v);
  const setTankGal = (v) => set('tankGal', v);
  const setTankGPM = (v) => set('tankGPM', v);
  const setTCap = (v) => set('tCap', v);
  const setFill = (v) => set('fill', v);
  const setDump = (v) => set('dump', v);
  const setDist = (v) => set('dist', v);
  const setSpd = (v) => set('spd', v);
  const setFloors = (v) => set('floors', v);
  const setSpGPM = (v) => set('spGPM', v);
  const setSpNP = (v) => set('spNP', v);

  const durMin = tankGPM > 0 ? tankGal / tankGPM : 0;
  const fillT = fill > 0 ? (tCap / fill) * 60 : 0;
  const dumpT = dump > 0 ? (tCap / dump) * 60 : 0;
  const travelT = spd > 0 ? (dist / spd) * 3600 : 0;
  const cycleT = fillT + dumpT + travelT * 2;
  const shuttleGPM = cycleT > 0 ? (tCap / cycleT) * 60 : 0;
  const floorLoss = (floors - 1) * floorLossPer;
  const spFL = calcFL(HOSE['2½"'].c, spGPM, 100);
  const spPDP = spNP + floorLoss + spAppLoss + spFL;

  return (
    <div>
      <SecHd title="Water Supply" sub="Tank duration, tanker shuttle, and standpipe/FDC in one cleaner screen." />
      <div className="hc-grid-3" style={{ gap: 6, marginBottom: 20 }}>
        {[['tank', 'Tank'], ['shuttle', 'Shuttle'], ['standpipe', 'Standpipe']].map(([id, label]) => (
          <button key={id} onClick={() => setMode(id)} style={{ background: mode === id ? K.red : K.bg2, border: `1px solid ${mode === id ? K.red2 : K.bdr}`, color: mode === id ? K.t0 : K.t1, padding: '12px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontFamily: K.fSans, fontWeight: 600, letterSpacing: 0.2 }}>{label}</button>
        ))}
      </div>

      {mode === 'tank' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div><Lbl t="Tank Size (gal)" /><NI v={tankGal} set={setTankGal} min={0} max={10000} /></div>
            <div><Lbl t="Flow Rate (GPM)" /><NI v={tankGPM} set={setTankGPM} min={1} max={2000} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Metric label="Duration" value={`${durMin.toFixed(1)} min`} sub={`${(durMin * 60).toFixed(0)} sec`} col={durMin < 2 ? K.bad : durMin < 5 ? K.warn : K.ok} />
            <Metric label="Flow" value={`${tankGPM} GPM`} sub={`${tankGal} gal tank`} col={K.info} sm />
          </div>
          <MathBox show={showMath} steps={[{ l: 'Duration', f: `${tankGal}÷${tankGPM}`, r: `${durMin.toFixed(1)} min` }]} />
        </>
      ) : null}

      {mode === 'shuttle' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div><Lbl t="Tanker Capacity (gal)" /><NI v={tCap} set={setTCap} min={500} max={10000} /></div>
            <div><Lbl t="Fill Site Rate (GPM)" /><NI v={fill} set={setFill} min={100} max={3000} /></div>
            <div><Lbl t="Dump Site Rate (GPM)" /><NI v={dump} set={setDump} min={100} max={3000} /></div>
            <div><Lbl t="Round Trip (mi)" /><NI v={dist} set={setDist} min={0.1} max={100} step={0.1} dec /></div>
            <div><Lbl t="Avg Speed (mph)" /><NI v={spd} set={setSpd} min={5} max={60} /></div>
          </div>
          <Metric label="Sustained Flow" value={`${shuttleGPM.toFixed(0)} GPM`} sub={`Cycle ${(cycleT / 60).toFixed(1)} min`} col={shuttleGPM > 250 ? K.ok : shuttleGPM > 100 ? K.warn : K.bad} />
          <MathBox show={showMath} steps={[
            { l: 'Fill', f: `${tCap}÷${fill}×60`, r: `${fillT.toFixed(0)} sec` },
            { l: 'Dump', f: `${tCap}÷${dump}×60`, r: `${dumpT.toFixed(0)} sec` },
            { l: 'Travel', f: `(${dist}÷${spd})×3600×2`, r: `${(travelT * 2).toFixed(0)} sec` },
            { l: 'Cycle', f: 'fill+dump+travel', r: `${(cycleT / 60).toFixed(1)} min` },
            { l: 'GPM', f: `(${tCap}÷${cycleT.toFixed(0)})×60`, r: `${shuttleGPM.toFixed(0)} GPM` },
          ]} />
        </>
      ) : null}

      {mode === 'standpipe' ? (
        <>
          <div className="hc-grid-3" style={{ marginBottom: 14 }}>
            <div><Lbl t="Target Floor" /><NI v={floors} set={setFloors} min={1} max={50} /></div>
            <div><Lbl t="Flow (GPM)" /><NI v={spGPM} set={setSpGPM} min={0} max={500} /></div>
            <div><Lbl t="NP (psi)" /><NI v={spNP} set={setSpNP} min={50} max={200} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <Metric label="Floor Loss" value={`${floorLoss} psi`} sub={`${floors - 1} × 5`} col={K.t1} sm />
            <Metric label="Standpipe App" value="25 psi" col={K.t1} sm />
            <Metric label="Hose FL" value={`${spFL.toFixed(1)} psi`} sub='2½" × 100ft' col={K.t1} sm />
            <Metric label="FDC PDP" value={`${spPDP.toFixed(1)} psi`} col={K.gold} />
          </div>
          <MathBox show={showMath} steps={[
            { l: 'Floor loss', f: `(${floors}−1)×5`, r: `${floorLoss} psi` },
            { l: 'SP App', f: 'fixed', r: '25 psi' },
            { l: 'Hose FL', f: `2×(${spGPM}/100)²×1`, r: `${spFL.toFixed(1)} psi` },
            { l: 'FDC PDP', f: `${spNP}+${floorLoss}+25+${spFL.toFixed(1)}`, r: `${spPDP.toFixed(1)} psi` },
          ]} />
        </>
      ) : null}
    </div>
  );
}
